/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { CenteredLoader, IfScope, useFeatureFlags } from '@playbookux/playbook-web-core';
import { CREATE_PREVIEW_STUDY } from './graphql';
import {
  Column,
  Container,
  Grid,
  Row,
  FieldName,
  FieldValue,
  Link,
  OpenExternal,
  LoaderWrapper,
} from './styles';

import {
  setProjectValues,
  clearQuestions,
} from '../../../../core/actions';
import { config } from '../../../../config';

import Modal from './components/cap-responses-modal';
import ModalPayment from './components/unmoderated-participants-modal';
import RecruitLinkCard from '../../../components/recruit-link-card';
import ProjectCard from '../../../components/project-card';
import QuestionsCard from '../../../components/questions-card';
import { languages, operatingSystems } from '../../../components/audience/options';
import withProjectSidebar from '../../../components/with-project-sidebar';
import IfPlan from '../../../../core/components/If-plan';
import Tabs from '../../../constants/tabs';
import EditProjectForm from './components/edit-project-form';
import Plans from '../../../../core/constants/plans';
import TestTypes from '../../../../core/constants/testTypes';
import ParticipantsSource from '../../../../core/constants/participantsSource';
import ProjectSummarySections from '../../../components/project-summary-sections';
import SaveTemplateModal from '../../../components/save-template-modal';
import typeTemplate from '../../../../core/constants/templateType';
import Collaborators from '../../../components/collaborators';
import WelcomeSection from '../../../components/project-summary-sections/components/project-survey-welcome-section';
import EndScreenSection from '../../../components/project-summary-sections/components/project-survey-end-screen-section';
import SurveysPagesSection from '../../../components/project-summary-sections/components/project-surveys-pages';
import { countryOptions } from '../../../../tester/pages/sign-up-survey/options';
import ScreenerRate from './components/screener-rate';
import Attachments from '../../../components/attachments';

const titleSegment = (segmentName, index) => {
  const title = `Audience Segment ${index + 1}`;
  if (segmentName.toLowerCase() === title.toLowerCase()) {
    return title;
  }
  return `${title}: ${segmentName}`;
};

const ProjectStudy = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const [templateType, setTemplateType] = useState('audience');
  const [segmentAudience, setSegmentAudience] = useState({});
  const [audienceAttributes, setAudienceAttributes] = useState([]);
  const [device, setDevice] = useState('computer');
  const [segmentId, setSegmentId] = useState(null);
  const [operatingSystem, setOperatingSystem] = useState([]);
  const [screener, setScreener] = useState({});
  const [token, setToken] = useState('');
  const [isRefetching, setIsRefetching] = useState(false);

  const {
    project,
    history,
    refetch,
    scope,
  } = props;

  const {
    createdBy,
    testType,
    otherTesterRequirements,
    sessionDuration,
    company,
    questionTemplate,
    recruitParticipantToken = {},
    id: projectId,
    surveys,
  } = project;

  const { featureFlags } = useFeatureFlags(company.id);

  const deviceType = {
    computer: 'Desktop',
    tablet: 'Tablet',
    smartphone: 'Mobile',
  };

  const baseUrl = config.REACT_APP_PLATFORM_URI;

  const surveyPageUrl = `${baseUrl}/create-project/${projectId}`;

  const [surveysPages] = surveys;

  const [createPreviewStudy] = useMutation(CREATE_PREVIEW_STUDY);

  const buildOperatingSystemLabel = (os) => (
    _.map(os, (x) => operatingSystems[x]).join(', ')
  );

  const handleToggleModal = (values) => {
    if (!_.isEmpty(values)) {
      setSegmentAudience(values);
    }
    setIsModalOpen(!isModalOpen);
  };

  const getAudienceAttributeOptions = (audienceAtt, type) => {
    const audienceAttribute = _.find(audienceAtt, { audienceAttributeType: type });

    if (!audienceAttribute) {
      return 'Any';
    }

    const { audienceAttributeOptions = {}, audienceAttributeType = {} } = audienceAttribute || {};

    return audienceAttributeOptions.map((option) => {
      if (audienceAttributeType === 'location_country') {
        const selectedCountry = countryOptions.find((country) => country.value === option.name);
        return selectedCountry?.text;
      }
      return option.name;
    }).join(', ');
  };

  const isUnitedStatesSelected = (locationCountryString) => (
    locationCountryString === 'Any' || locationCountryString.includes('United States')
  );

  const getLanguage = (languageValue) => (
    _.find(languages, (language) => language.value === languageValue).text
  );

  const goToEditQuestions = () => {
    history.push({
      pathname: `/projects/${project.id}/questions`,
    });
  };

  const goToEditScreener = (segmentIndex) => {
    history.push({
      pathname: `/projects/${project.id}/segment/${project.segments[segmentIndex].id}/screener`,
    });
  };

  const goToEditSurveyPageQuestions = (surveyPage) => {
    history.push({
      pathname: `/projects/${project.id}/questions`,
      state: surveyPage,
    });
  };

  const getScreener = (segment) => _.find(segment.surveys, { surveyType: 'screener' }) || {};

  const isScreenerEmpty = (segments) => !segments.some((segment) => segment.surveys.some((survey) => survey.surveyType === 'screener'));

  const handleSaveTemplate = (type, segment = {}) => {
    setTemplateType(type);
    setIsSaveTemplateModalOpen(true);
    setAudienceAttributes(segment.audienceAttributes);
    setDevice(segment.device);
    setSegmentId(segment.id);
    setOperatingSystem(segment.operatingSystem);
    setScreener(getScreener(segment));
  };

  const handleSaveTemplateModalClose = () => {
    setIsSaveTemplateModalOpen(false);
  };

  const session = useMemo(() => _.find(project.surveys, { surveyType: 'session' }) || {}, [project]);

  const surveyPages = useMemo(() => (
    surveysPages?.surveysPages
      ? _.orderBy(surveysPages.surveysPages, ['order'], ['asc'])
      : []
  ), [surveysPages]);

  const orderedSurveyPages = useMemo(() => _.orderBy(surveyPages, ['order'], ['asc']), [surveyPages]);

  const handlePreview = () => {
    createPreviewStudy({ variables: { projectId } })
      .then((response) => {
        const { data: previewStudy, error: previewStudyError } = response;

        if (previewStudy && !previewStudyError) {
          const { testSession } = previewStudy.createCardSortPreviewStudy;
          const sessionId = testSession.id;
          window.open(`${baseUrl}/welcome?session-id=${sessionId}`);
        }
      });
  };

  useEffect(() => {
    if (!_.isEmpty(recruitParticipantToken)) {
      setToken(recruitParticipantToken.token);
    }
  }, [recruitParticipantToken]);

  useEffect(() => {
    if (history.location.state?.shouldRefetch) {
      setIsRefetching(true);

      refetch()
        .then(() => setIsRefetching(false));
    }
  }, [history, refetch]);

  return (
    <Container>
      {project.participantsSource === ParticipantsSource.COMPANY
        && testType !== TestTypes.MODERATED
        && testType !== TestTypes.UPLOAD
        && token
        && (
          <RecruitLinkCard
            testType={testType}
            title="URL"
            url={`${config.REACT_APP_TESTER_PLATFORM_URI}/recruit-participants?token=${token}`}
          />
        )}

      {isRefetching
        ? (
          <LoaderWrapper>
            <CenteredLoader data-loader="project-study-loader" />
          </LoaderWrapper>
        )
        : (
          <>
            <ProjectCard
              title="Details"
            >
              <Grid columns={2}>
                <IfScope user scope={scope}>
                  <EditProjectForm
                    project={project}
                  />
                </IfScope>
                <IfScope shareProject scope={scope}>
                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>
                        Project Name
                      </FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{project.name}</FieldValue>
                    </Column>
                  </Row>

                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Description</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>{project.description}</FieldValue>
                    </Column>
                  </Row>

                </IfScope>

                {testType === 'card_sort' && (
                  <Row>
                    <Column computer={4} tablet={4} mobile={8}>
                      <FieldName>Card Sort Type</FieldName>
                    </Column>
                    <Column computer={12} tablet={12} mobile={8}>
                      <FieldValue>
                        {project.sortType === 'opened' ? 'Open' : _.capitalize(project.sortType)}
                      </FieldValue>
                    </Column>
                  </Row>
                )}

                <Row>
                  <Column computer={4} tablet={4} mobile={8}>
                    <FieldName>{project.launchedOn ? 'Launched on' : 'Created on'}</FieldName>
                  </Column>
                  <Column computer={12} tablet={12} mobile={8}>
                    <FieldValue>
                      {project.launchedOn ? moment(project.launchedOn).format('L') : moment(project.createdAt).format('L')}
                    </FieldValue>
                  </Column>
                </Row>

                <Row>
                  <Column computer={4} tablet={4} mobile={8}>
                    <FieldName>Created by</FieldName>
                  </Column>
                  <Column computer={12} tablet={12} mobile={8}>
                    <FieldValue>
                      {`${createdBy.profile.firstName} ${createdBy.profile.lastName}`}
                    </FieldValue>
                  </Column>
                </Row>

                {project.participantsSource === ParticipantsSource.COMPANY && testType !== TestTypes.UPLOAD && (
                  <>
                    <Row>
                      <Column computer={4} tablet={4} mobile={8}>
                        <FieldName>Cap Number of Responses</FieldName>
                      </Column>
                      <Column computer={1} tablet={1} mobile={1}>
                        <FieldValue>{project.audienceParticipants}</FieldValue>
                      </Column>

                      <IfScope user scope={scope}>
                        <IfPlan plans={[Plans.GROW, Plans.SCALE]} company={company}>

                          <Column computer={11} tablet={11} mobile={7}>
                            <Link
                              onClick={handleToggleModal}
                            >
                              Change Response Cap
                            </Link>
                          </Column>

                        </IfPlan>
                      </IfScope>
                    </Row>

                    <Modal
                      open={isModalOpen && project.participantsSource === ParticipantsSource.COMPANY}
                      onClose={handleToggleModal}
                      projectId={project.id}
                      segmentId={project.segments[0].id}
                      audienceParticipants={project.audienceParticipants}
                      onSubmit={() => setIsModalOpen(false)}
                    />

                  </>
                )}
              </Grid>
            </ProjectCard>

            {featureFlags.uploadResearch && (
              <Attachments scope={scope} />
            )}

            {testType === TestTypes.MODERATED && (
              <Collaborators
                project={project}
                companyId={company.id}
                plan={company.plan}
                stripeCustomerId={company.stripeCustomerId}
              />
            )}

            {project.participantsSource === ParticipantsSource.PLAYBOOK
              && project.testType !== TestTypes.UPLOAD
              && project.segments.map((segment, index) => {
                const screenerSegment = getScreener(segment);
                let questionsSegment = [];
                if (screenerSegment) questionsSegment = _.orderBy(screenerSegment.questions, ['questionOrder']);
                const isQuestionsSegmentEmpty = _.isEmpty(questionsSegment);
                const locationCountryString = getAudienceAttributeOptions(segment.audienceAttributes, 'location_country');

                return (
                  <ProjectCard
                    title={`${titleSegment(segment.name, index) }`}
                    onSaveTemplate={() => handleSaveTemplate(typeTemplate.TEMPLATE_AUDIENCE, segment)}
                    textButton="Save Audience"
                    isTemplateSaved={segment.audienceTemplate}
                    edit={!isQuestionsSegmentEmpty ? () => goToEditScreener(index) : null}
                    editText="Edit Screener"
                    icon="external alternate"
                    key={segment.id}
                    type={project.testType}
                    completedTests={segment.countApprovedTesters}
                    totalTests={segment.audienceParticipants}
                  >
                    <Grid columns={2}>
                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Number of Participants</FieldName>
                        </Column>
                        <Column computer={1} tablet={1} mobile={1}>
                          <FieldValue>{segment.audienceParticipants}</FieldValue>
                        </Column>

                        <IfScope user scope={scope}>
                          <Column computer={11} tablet={11} mobile={7}>
                            <Link
                              onClick={() => { handleToggleModal(segment); }}
                            >
                              Add Participants
                            </Link>
                          </Column>
                        </IfScope>
                        {isModalOpen && project.participantsSource !== ParticipantsSource.COMPANY && (
                          <ModalPayment
                            open
                            onClose={handleToggleModal}
                            project={project}
                            onSubmit={() => setIsModalOpen(false)}
                            sessionDuration={sessionDuration}
                            testType={testType}
                            device={segment.device}
                            refetchProject={refetch}
                            segmentAudience={segmentAudience}
                          />
                        )}

                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Device</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{deviceType[segment.device]}</FieldValue>
                        </Column>
                      </Row>
                      {segment.device !== 'computer' && (
                        <Row>
                          <Column computer={4} tablet={4} mobile={8}>
                            <FieldName>Operating System</FieldName>
                          </Column>
                          <Column computer={12} tablet={12} mobile={8}>
                            <FieldValue>{buildOperatingSystemLabel(segment.operatingSystem)}</FieldValue>
                          </Column>
                        </Row>
                      )}
                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Age</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'age')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Gender</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'gender')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Ethnicity</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'ethnicity')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Household Income</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'household_income')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Country</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{locationCountryString}</FieldValue>
                        </Column>
                      </Row>

                      {isUnitedStatesSelected(locationCountryString) && (
                        <Row>
                          <Column computer={4} tablet={4} mobile={8}>
                            <FieldName>US States</FieldName>
                          </Column>
                          <Column computer={12} tablet={12} mobile={8}>
                            <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'location_state')}</FieldValue>
                          </Column>
                        </Row>
                      )}

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Language</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>
                            {getLanguage(segment.participantsLanguage)}
                          </FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Job Role</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'job_role')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Current Work Status</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'work_status')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Company Size</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'company_size')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Industry</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'industry')}</FieldValue>
                        </Column>
                      </Row>

                      <Row>
                        <Column computer={4} tablet={4} mobile={8}>
                          <FieldName>Seniority</FieldName>
                        </Column>
                        <Column computer={12} tablet={12} mobile={8}>
                          <FieldValue>{getAudienceAttributeOptions(segment.audienceAttributes, 'seniority')}</FieldValue>
                        </Column>
                      </Row>

                      {screenerSegment && !_.isEmpty(screenerSegment.questions) && (
                        <>
                          {questionsSegment.map((question, questionIndex) => (
                            <ScreenerRate
                              key={question.id}
                              question={question}
                              questionIndex={questionIndex}
                              title="Screener Question"
                            />
                          ))}
                          <OpenExternal
                            label="View screener versions"
                            href={`${baseUrl}/projects/${projectId}/segment/${segment.id}/screeners`}
                          />
                        </>
                      )}
                    </Grid>

                    {isSaveTemplateModalOpen && (
                      <SaveTemplateModal
                        isOpen={isSaveTemplateModalOpen}
                        onClose={handleSaveTemplateModalClose}
                        questions={session.questions}
                        companyId={company.id}
                        templateType={templateType}
                        templateContext={project.studyContext}
                        projectId={projectId}
                        segmentId={segmentId}
                        refetchProject={refetch}
                        audienceAttributes={audienceAttributes}
                        device={device}
                        operatingSystem={operatingSystem}
                        screener={screener}
                        participantsLanguage={segment.participantsLanguage}
                        otherTesterRequirements={otherTesterRequirements}
                      />
                    )}
                  </ProjectCard>
                );
              })}

            {project.participantsSource === ParticipantsSource.COMPANY
              && project.testType !== TestTypes.UPLOAD
              && (
                <ProjectCard
                  title="Screener Questions"
                  edit={!isScreenerEmpty(project.segments) ? () => goToEditScreener(0) : null}
                  editText="Edit Screener"
                  icon="external alternate"
                >
                  {project.segments.map((segment) => {
                    const screenerSegment = getScreener(segment);
                    let questionsSegment = [];
                    if (screenerSegment) questionsSegment = _.orderBy(screenerSegment.questions, ['questionOrder']);
                    const isQuestionsSegmentEmpty = _.isEmpty(questionsSegment);

                    return (
                      <>
                        {(!screenerSegment || _.isEmpty(questionsSegment)) && (
                          <FieldValue key={segment.id}>
                            No screener questions found.
                          </FieldValue>
                        )}
                        {screenerSegment && !_.isEmpty(questionsSegment) && (
                          <Grid columns={2} key={segment.id}>
                            {questionsSegment.map((question, questionIndex) => (
                              <ScreenerRate
                                key={question.id}
                                question={question}
                                questionIndex={questionIndex}
                                title="Question"
                              />
                            ))}
                          </Grid>
                        )}
                        {!isQuestionsSegmentEmpty && (
                          <OpenExternal
                            label="View screener versions"
                            href={`${baseUrl}/projects/${projectId}/segment/${segment.id}/screeners`}
                          />
                        )}

                        {isSaveTemplateModalOpen && (
                          <SaveTemplateModal
                            isOpen={isSaveTemplateModalOpen}
                            onClose={handleSaveTemplateModalClose}
                            questions={session.questions}
                            companyId={company.id}
                            templateType={templateType}
                            templateContext={project.studyContext}
                            projectId={projectId}
                            segmentId={segmentId}
                            refetchProject={refetch}
                            audienceAttributes={audienceAttributes}
                            device={device}
                            operatingSystem={operatingSystem}
                            screener={screener}
                            participantsLanguage={segment.participantsLanguage}
                            otherTesterRequirements={otherTesterRequirements}
                          />
                        )}
                      </>
                    );
                  })}
                </ProjectCard>
              )}

            {testType === TestTypes.CARD_SORT && (
              <ProjectSummarySections
                values={project}
                testType={TestTypes.CARD_SORT}
                smallColumn
              />
            )}

            {testType === TestTypes.TREE_TEST && (
              <ProjectSummarySections
                values={project}
                testType={TestTypes.TREE_TEST}
                onPreviewStudy={handlePreview}
                smallColumn
              />
            )}

            {(testType === TestTypes.UNMODERATED) && (
              <>
                <QuestionsCard
                  edit={goToEditQuestions}
                  questions={_.orderBy(session.questions, 'questionOrder')}
                  context={project.studyContext}
                  editText="Edit Questions"
                  icon="external alternate"
                  onSaveTemplate={() => handleSaveTemplate(typeTemplate.TEMPLATE_TEST)}
                  isTemplateSaved={questionTemplate}
                  textButton="Save as Template"
                />
              </>
            )}

            {(testType === TestTypes.SURVEY && orderedSurveyPages) && (
              <>
                <WelcomeSection
                  history={history}
                  values={project}
                  smallColumn
                />

                {orderedSurveyPages && orderedSurveyPages.map((surveyPage, index) => (
                  <SurveysPagesSection
                    key={surveyPage.id}
                    baseUrl={surveyPageUrl}
                    history={history}
                    values={surveyPage}
                    editText="Edit Questions"
                    icon="external alternate"
                    smallColumn
                    index={index + 1}
                    edit={() => { goToEditSurveyPageQuestions(surveyPage); }}
                  />
                ))}

                <EndScreenSection
                  history={history}
                  values={project}
                  smallColumn
                />

              </>
            )}
          </>
        )}
    </Container>
  );
};

ProjectStudy.propTypes = {
  project: PropTypes.shape().isRequired,
  history: PropTypes.shape().isRequired,
  refetch: PropTypes.func.isRequired,
  scope: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  scope: state.authentication && state.authentication.scope,
});

const mapDispatchToProps = {
  onClearQuestions: clearQuestions,
  onSetProject: setProjectValues,
};

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProjectSidebar({ selectedTab: Tabs.PROJECT }),
);

export default enhance(ProjectStudy);
